import {
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Tfoot,
  Icon,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { ChevronSelectorVertical } from '@kula-ai/icons/outline/ChevronSelectorVertical';
import '../styles/tableLayout.css';

// react-table v7 instances are loosely typed here; keep the surface permissive
// to mirror the original JS contract rather than fully modelling react-table.
interface TableLayoutProps {
  tableInstance?: any;
  className?: string;
  layoutStyles?: Record<string, any>;
  isRowSelectable?: boolean;
  isColumnLevelSorting?: boolean;
  enableRowAction?: boolean;
  layoutScrollContainerStyles?: Record<string, any>;
  tableLayoutStyles?: Record<string, any>;
  tableStyles?: Record<string, any>;
  _handleSelectionState?: (..._args: any[]) => void;
  _type?: any;
  _pageLimit?: any;
  _totalCount?: any;
  tableRef?: any;
  _isLoadingMore?: boolean;
  showFooter?: boolean;
  rowStyles?: Record<string, any> | ((..._args: any[]) => Record<string, any>);
}

export const TableLayout = ({
  tableInstance = {},
  className,
  layoutStyles,
  isRowSelectable = false,
  isColumnLevelSorting = false,
  enableRowAction = false,
  layoutScrollContainerStyles = {},
  tableLayoutStyles = {},
  tableStyles = {},
  _handleSelectionState = () => {},
  _type,
  _pageLimit,
  _totalCount,
  tableRef,
  _isLoadingMore = false,
  showFooter = false,
  rowStyles = {},
}: TableLayoutProps) => {
  const {
    getTableProps = () => {},
    getTableBodyProps = () => {},
    headerGroups = [],
    prepareRow = () => {},
    rows = [],
    footerGroups = [],
  } = tableInstance;

  const stickyStyles = {
    position: 'sticky',
    left: '0',
    zIndex: 2,
  };

  return (
    <Box
      height="100%"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="border-color"
      {...layoutStyles}
    >
      <div className="tableLayout" {...tableLayoutStyles}>
        <div
          className="tableLayout-scroll-container"
          {...layoutScrollContainerStyles}
          ref={tableRef}
        >
          <div
            className={`${isRowSelectable ? 'rowSelect' : ''} ${className} ${
              enableRowAction ? 'rowActionItem' : ''
            }`}
          >
            <Table {...getTableProps()} sx={tableStyles}>
              <Thead>
                {headerGroups.map((headerGroup) => {
                  const { key: headerGroupKey, ...headerGroupProps } =
                    headerGroup.getHeaderGroupProps();
                  return (
                    <Tr key={headerGroupKey} {...headerGroupProps}>
                      {headerGroup.headers.map((column, columnIndex) => {
                        const headerProps = column.getHeaderProps(
                          isColumnLevelSorting && column.sort
                            ? column.getSortByToggleProps()
                            : undefined
                        );
                        const { key: columnKey, ...restHeaderProps } =
                          headerProps;
                        return (
                          <Th
                            key={columnKey}
                            {...restHeaderProps}
                            style={{
                              ...headerProps.style,
                              ...(column.headerStyle || {}),
                              ...(columnIndex === (isRowSelectable ? 1 : 0)
                                ? { borderRight: 'none' }
                                : {}),
                              ...(columnIndex === 0
                                ? isRowSelectable
                                  ? { ...stickyStyles, paddingRight: '15px' }
                                  : stickyStyles
                                : columnIndex === 1 && isRowSelectable
                                  ? { ...stickyStyles, left: '25px' }
                                  : {}),
                              ...(column?.style || {}),
                            }}
                          >
                            {column.render('Header')}
                            {isColumnLevelSorting && column.sort && (
                              <Box
                                ml="4px"
                                display="inline-flex"
                                alignItems="center"
                                className={`sort-icon-container ${
                                  column.isSorted ? 'sorted' : ''
                                }`}
                              >
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <ChevronUpIcon width="16px" height="16px" />
                                  ) : (
                                    <ChevronDownIcon
                                      width="16px"
                                      height="16px"
                                    />
                                  )
                                ) : (
                                  <Icon
                                    as={ChevronSelectorVertical}
                                    w="16px"
                                    h="16px"
                                  />
                                )}
                              </Box>
                            )}
                          </Th>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {rows?.map((row, rowIndex) => {
                  prepareRow(row);
                  // Support rowStyles as both object and function
                  const computedRowStyles =
                    typeof rowStyles === 'function'
                      ? rowStyles(row.original, row)
                      : rowStyles;

                  // Extract className and other style props
                  const { className: rowClassName, ...styleProps } =
                    computedRowStyles || {};

                  return (
                    <Tr
                      {...row.getRowProps()}
                      role="group"
                      data-test-id={`table-row-${rowIndex}`}
                      key={`table-row-${rowIndex}`}
                      className={rowClassName || ''}
                      style={{
                        ...row.getRowProps().style,
                        ...styleProps,
                      }}
                    >
                      {row.cells.map((cell, cellIndex) => {
                        const cellProps = cell.getCellProps();
                        const { key: cellKey, ...restCellProps } = cellProps;
                        return (
                          <Td
                            key={cellKey}
                            data-test-id={`table-row-${rowIndex}-cell-${cellIndex}`}
                            {...restCellProps}
                            style={{
                              ...cellProps.style,
                              ...(cellIndex === (isRowSelectable ? 1 : 0)
                                ? {
                                    borderRight:
                                      '1px solid var(--kula-colors-border-color)',
                                  }
                                : {}),
                              ...(cellIndex === 0
                                ? isRowSelectable
                                  ? { ...stickyStyles, paddingRight: '15px' }
                                  : stickyStyles
                                : cellIndex === 1 && isRowSelectable
                                  ? { ...stickyStyles, left: '25px' }
                                  : {}),
                              ...(cell?.column?.style || {}),
                            }}
                          >
                            {cell.render('Cell')}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
              {showFooter && (
                <Tfoot>
                  <Tr {...footerGroups?.[0]?.getHeaderGroupProps()}>
                    {footerGroups[0].headers.map((column, columnIndex) => (
                      <>
                        {column?.Footer && (
                          <Td
                            {...column.getFooterProps()}
                            {...column.getHeaderProps()}
                            style={{
                              ...column.getFooterProps().style,
                              ...(columnIndex === (isRowSelectable ? 1 : 0)
                                ? {
                                    borderRight:
                                      '1px solid var(--kula-colors-border-color)',
                                  }
                                : {}),
                              ...(columnIndex === 0
                                ? stickyStyles
                                : columnIndex === 1 && isRowSelectable
                                  ? { ...stickyStyles, left: '25px' }
                                  : {}),
                              ...(column?.column?.style || {}),
                            }}
                          >
                            {column.render('Footer')}
                          </Td>
                        )}
                      </>
                    ))}
                  </Tr>
                </Tfoot>
              )}
            </Table>
            <Box id="table-pagination-spacer" display="none" h="44px" />
          </div>
        </div>
      </div>
    </Box>
  );
};
